// Phase Response Card 1.0 Lite — Studio control panel.
// Collapsed by default — only opens when the designer explicitly clicks the
// header. The basic Response Card editing experience stays clean for events
// that don't need a custom background.

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Upload, Trash2, Loader2,
} from 'lucide-react';
import {
  DEFAULT_RSVP_BACKGROUND, RSVP_SURFACE_TEXTURES, RSVP_SURFACE_LABELS,
  RSVP_OVERLAY_OPTIONS, updateRSVPBackground, resetRSVPBackground,
} from '../../lib/utils/responseCardBackground';

const API = process.env.REACT_APP_BACKEND_URL;

const SEG = 'inline-flex items-center justify-center px-2 py-1 text-[11px] font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300';
const SEG_ON = 'bg-violet-600 text-white border-violet-600 dark:border-violet-500';

function Segmented({ value, options, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md overflow-hidden" data-testid={testid}>
      {options.map((o, i) => (
        <button
          type="button"
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            SEG,
            value === o.value ? SEG_ON : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
            i === 0 ? 'rounded-l-md' : '-ml-px',
            i === options.length - 1 ? 'rounded-r-md' : '',
          ].join(' ')}
          data-testid={`${testid}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function ResponseCardBackgroundPanel({ eventId, token, background, onChange }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bg = { ...DEFAULT_RSVP_BACKGROUND, ...(background || {}) };

  const patch = (p) => onChange(updateRSVPBackground(bg, p));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        form,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      patch({ type: 'custom_image', imageUrl: r.data.url });
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = ''; // allow re-upload of the same file
    }
  };

  const remove = () => {
    onChange(resetRSVPBackground());
    toast.success('Background removed');
  };

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="response-card-background-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
        data-testid="response-card-background-panel-toggle"
      >
        <div className="min-w-0">
          <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">Background / Surface</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {bg.type === 'none' && 'No background'}
            {bg.type === 'surface_texture' && (RSVP_SURFACE_LABELS[bg.texture] || 'Surface texture')}
            {bg.type === 'custom_image' && 'Custom image'}
          </div>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        }
      </button>

      {open && (
        <div className="p-3 space-y-3 border-t border-gray-200 dark:border-gray-800">
          <Field label="Type">
            <Segmented
              value={bg.type}
              options={[
                { value: 'none',            label: 'None' },
                { value: 'surface_texture', label: 'Surface' },
                { value: 'custom_image',    label: 'Custom image' },
              ]}
              onChange={(t) => {
                if (t === 'none') return remove();
                if (t === 'surface_texture') return patch({ type: t, texture: bg.texture || 'cotton_paper', imageUrl: undefined });
                if (t === 'custom_image')    return patch({ type: t, texture: undefined });
              }}
              testid="rcb-type"
            />
          </Field>

          {bg.type === 'surface_texture' && (
            <Field label="Surface">
              <div className="grid grid-cols-3 gap-2" data-testid="rcb-surface-grid">
                {Object.entries(RSVP_SURFACE_TEXTURES).map(([key, url]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => patch({ texture: key })}
                    className={`relative aspect-square rounded-md overflow-hidden border-2 ${bg.texture === key ? 'border-violet-600' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-700'}`}
                    style={{ backgroundImage: `url(${url})`, backgroundSize: '256px 256px' }}
                    data-testid={`rcb-surface-${key}`}
                  >
                    <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[9px] uppercase tracking-wider font-medium bg-black/40 text-white text-center">
                      {RSVP_SURFACE_LABELS[key]}
                    </div>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {bg.type === 'custom_image' && (
            <Field label="Image">
              {bg.imageUrl ? (
                <div className="flex items-center gap-2">
                  <div
                    className="w-14 h-14 rounded-md border border-gray-200 dark:border-gray-700 bg-cover bg-center shrink-0"
                    style={{ backgroundImage: `url(${bg.imageUrl})` }}
                  />
                  <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Replace
                    <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="rcb-image-replace" disabled={uploading} />
                  </label>
                  <button
                    type="button"
                    onClick={() => patch({ imageUrl: undefined })}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                    data-testid="rcb-image-remove"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-violet-600 text-white hover:bg-violet-700 cursor-pointer">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Upload image
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="rcb-image-upload" disabled={uploading} />
                </label>
              )}
              <p
                className="mt-1.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400"
                data-testid="rcb-image-helper"
              >
                Recommended: vertical image, at least 1200px wide. Large uploads are optimized automatically.
              </p>
            </Field>
          )}

          {bg.type !== 'none' && (
            <Field label="Apply to">
              <Segmented
                value={bg.target}
                options={[
                  { value: 'card',    label: 'Card' },
                  { value: 'section', label: 'Section' },
                ]}
                onChange={(v) => patch({ target: v })}
                testid="rcb-target"
              />
            </Field>
          )}

          {bg.type === 'custom_image' && bg.imageUrl && (
            <>
              <Field label="Fit">
                <Segmented
                  value={bg.fit}
                  options={[
                    { value: 'cover',   label: 'Cover' },
                    { value: 'contain', label: 'Contain' },
                  ]}
                  onChange={(v) => patch({ fit: v })}
                  testid="rcb-fit"
                />
              </Field>
              <Field label="Position">
                <Segmented
                  value={bg.position}
                  options={[
                    { value: 'center', label: 'Center' },
                    { value: 'top',    label: 'Top' },
                    { value: 'bottom', label: 'Bottom' },
                  ]}
                  onChange={(v) => patch({ position: v })}
                  testid="rcb-position"
                />
              </Field>
            </>
          )}

          {bg.type !== 'none' && (
            <>
              <Field label={`Opacity · ${Math.round((bg.opacity ?? 0.18) * 100)}%`}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.02"
                  value={bg.opacity ?? 0.18}
                  onChange={(e) => patch({ opacity: Number(e.target.value) })}
                  className="w-full accent-violet-600"
                  data-testid="rcb-opacity"
                />
              </Field>
              <Field label="Overlay wash">
                <Segmented
                  value={bg.overlay || 'none'}
                  options={RSVP_OVERLAY_OPTIONS}
                  onChange={(v) => patch({ overlay: v })}
                  testid="rcb-overlay"
                />
              </Field>
              <button
                type="button"
                onClick={remove}
                className="text-[11px] text-rose-600 hover:underline"
                data-testid="rcb-remove-all"
              >
                Remove background
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
